import {useCallback, useRef, useState} from 'react';

import {usePubSub} from '@netx/core-components/hooks/usePubSub';
import {
	PubSubExpirationAction,
	PubSubExpirationEvent,
	PubSubExpirationPayload,
} from './actions';

// TODO: suss out and move to core hooks if helpful
const useOperationsInProgress = <O extends string = string>() => {
	const [operationsInProgress, setOperationsInProgress] = useState<O[]>([]);
	const operationsInProgressRef = useRef(operationsInProgress);
	operationsInProgressRef.current = operationsInProgress;

	const add = useCallback((operation: O) => {
		setOperationsInProgress((operationsInProgress) => {
			if (operationsInProgress.includes(operation)) return operationsInProgress;
			return [...operationsInProgress, operation];
		});
	}, []);

	const remove = useCallback((operation: O) => {
		setOperationsInProgress((operationsInProgress) =>
			operationsInProgress.filter(
				(operationInProgress) => operationInProgress !== operation,
			),
		);
	}, []);

	return {
		add,
		remove,
		operations: operationsInProgress,
		inProgress: !!operationsInProgress.length,
	};
};

export const useAssetExpirationPubSub = () => {
	const {emit, subscribe} = usePubSub<PubSubExpirationEvent>();

	const assetExpirationOperations =
		useOperationsInProgress<PubSubExpirationAction>();
	const performAction = useCallback(
		async <
			A extends PubSubExpirationAction,
			C extends () => Promise<PubSubExpirationPayload<A>>,
		>(
			action: A,
			cb: C,
			options: {
				noEmit?: boolean;
			} = {},
		) => {
			const {noEmit} = options;
			assetExpirationOperations.add(action);
			try {
				const payload = await cb();
				!noEmit && emit({payload, action} as PubSubExpirationEvent);
				return payload;
			} finally {
				assetExpirationOperations.remove(action);
			}
		},
		[],
	);

	return {
		emit,
		performAction,
		subscribe,
		working: !!assetExpirationOperations.operations.length,
	};
};

export default useAssetExpirationPubSub;
